import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import ApiTestForm from './ApiTestForm'
import ApiLogPanel from './ApiLogPanel'
import DebugPanel from './DebugPanel'
import { logger } from '../utils/logger'

const { FiLogOut, FiMonitor, FiActivity, FiSettings, FiCode, FiDatabase } = FiIcons

const ApiMonitorDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('ncb')
  const [logs, setLogs] = useState([])
  const [isLogPanelOpen, setIsLogPanelOpen] = useState(true)

  useEffect(() => {
    // Subscribe to logger updates
    const unsubscribe = logger.subscribe((newLogs) => {
      setLogs(newLogs)
    })

    return unsubscribe
  }, [])

  const tabs = [
    { id: 'ncb', label: 'NoCodeBackend', icon: FiDatabase, color: 'text-blue-600' },
    { id: 'dropbox', label: 'Dropbox API', icon: FiCode, color: 'text-green-600' },
    { id: 'debug', label: 'Debug Panel', icon: FiSettings, color: 'text-purple-600' }
  ]

  const handleLogout = () => {
    logger.info('User initiated logout')
    onLogout()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <SafeIcon icon={FiMonitor} className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">API Monitor Platform</h1>
                <p className="text-sm text-gray-500">Real-time API testing & logging</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsLogPanelOpen(!isLogPanelOpen)}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isLogPanelOpen 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiActivity} className="h-4 w-4 mr-2" />
                Logs ({logs.length})
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`grid gap-6 transition-all duration-300 ${
          isLogPanelOpen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
        }`}>
          {/* Main Panel */}
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
              <nav className="flex space-x-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={tab.icon} className={`h-4 w-4 mr-2 ${tab.color}`} />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'debug' ? (
                <DebugPanel />
              ) : (
                <ApiTestForm apiType={activeTab} />
              )}
            </motion.div>
          </div>

          {/* Log Panel */}
          {isLogPanelOpen && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <ApiLogPanel logs={logs} />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ApiMonitorDashboard